// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  Button,
  Chip,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import BeerStylesTable from './BeerStylesTable';
import EditBeerStyleDialog from './EditBeerStyleDialog';
import useBeerStyles from '../../hooks/useBeerStyles';
import useBeerStyleTypes from '../../hooks/useBeerStyleTypes';
import { filterStylesNeedingTypeMapping } from '../../utils/beerStyle/styleNeedsTypeMapping';

const STORAGE_KEY = 'beerStyles.groupByType';
const NEEDS_TYPE_STORAGE_KEY = 'beerStyles.needsTypeOnly';

function readNeedsTypeOnlyFromSearch(searchParams) {
  return searchParams.get('needsType') === '1';
}

/**
 * BeerStyles management page listing all beer styles in a table.
 */
function BeerStyles() {
  const [searchParams] = useSearchParams();
  const { beerStyles, loading, error, updateBeerStyle, createBeerStyle } =
    useBeerStyles();
  const { styleTypes } = useBeerStyleTypes();
  const [groupByType, setGroupByType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'true';
  });
  const [needsTypeOnly, setNeedsTypeOnly] = useState(() => {
    if (readNeedsTypeOnlyFromSearch(searchParams)) {
      return true;
    }
    const saved = localStorage.getItem(NEEDS_TYPE_STORAGE_KEY);
    return saved === 'true';
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(groupByType));
  }, [groupByType]);

  useEffect(() => {
    localStorage.setItem(NEEDS_TYPE_STORAGE_KEY, String(needsTypeOnly));
  }, [needsTypeOnly]);

  useEffect(() => {
    if (readNeedsTypeOnlyFromSearch(searchParams)) {
      setNeedsTypeOnly(true);
    }
  }, [searchParams]);

  const needsTypeCount = useMemo(
    () => filterStylesNeedingTypeMapping(beerStyles).length,
    [beerStyles],
  );

  const displayedStyles = useMemo(() => {
    if (!needsTypeOnly) {
      return beerStyles;
    }
    return filterStylesNeedingTypeMapping(beerStyles);
  }, [beerStyles, needsTypeOnly]);

  const handleToggleGroupByType = useCallback((event) => {
    setGroupByType(event.target.checked);
  }, []);

  const handleToggleNeedsTypeOnly = useCallback((event) => {
    setNeedsTypeOnly(event.target.checked);
  }, []);

  const handleEditClick = useCallback((style) => {
    setIsCreateMode(false);
    setEditingStyle(style);
    setFormData({
      name: style.name || '',
      type: style.type || '',
      typeId: style.typeId || '',
    });
    setErrorMsg('');
    setEditDialogOpen(true);
  }, []);

  const handleCreateClick = useCallback(() => {
    setIsCreateMode(true);
    setEditingStyle({ id: '', name: '', type: '' });
    setFormData({ name: '', type: '' });
    setErrorMsg('');
    setEditDialogOpen(true);
  }, []);

  // Keyboard shortcut: 'c' key to create new style (when not typing in input)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if 'c' key is pressed and user is not typing in an input/textarea
      const isCKey = event.key === 'c' || event.key === 'C';
      const isModifierPressed = event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
      const isInputFocused =
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable;

      // Only trigger if 'c' is pressed alone (no modifiers) and not typing in input
      if (isCKey && !isModifierPressed && !isInputFocused) {
        event.preventDefault();
        handleCreateClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCreateClick]);

  const handleCloseDialog = useCallback(() => {
    setEditDialogOpen(false);
    setIsCreateMode(false);
    setEditingStyle(null);
    setFormData({ name: '', type: '' });
    setErrorMsg('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'type') {
        const matchedType = styleTypes.find((typeRow) => typeRow.name === value);
        updated.typeId = matchedType?.id || '';
      }
      return updated;
    });
  }, [styleTypes]);

  const handleSave = useCallback(async () => {
    if (!formData.name?.trim()) {
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      if (isCreateMode) {
        await createBeerStyle(formData.name.trim(), formData.type || null, formData.typeId || null);
      } else {
        if (!editingStyle?.id) {
          setErrorMsg('Invalid beer style ID');
          setSubmitting(false);
          return;
        }
        await updateBeerStyle(editingStyle.id, {
          name: formData.name.trim(),
          type: formData.type || null,
          typeId: formData.typeId || null,
        });
      }
      handleCloseDialog();
    } catch (err) {
      setErrorMsg(
        err.message || `Failed to ${isCreateMode ? 'create' : 'update'} beer style. Please try again.`,
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    isCreateMode,
    editingStyle,
    formData,
    createBeerStyle,
    updateBeerStyle,
    handleCloseDialog,
  ]);

  return (
    <DashboardLayout title="Beer Styles">
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2
          }}>
          <Typography variant="h5" component="h1">
            Beer Styles
            {needsTypeCount > 0 ? (
              <Chip
                label={`${needsTypeCount} need type`}
                color="warning"
                size="small"
                sx={{ ml: 1.5, verticalAlign: 'middle' }}
              />
            ) : null}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
            <Tooltip title="Create a new beer style (press 'c')">
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateClick}
              >
                Create Style
              </Button>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={needsTypeOnly}
                  onChange={handleToggleNeedsTypeOnly}
                />
              }
              label="Needs type only"
            />
            <FormControlLabel
              control={
                <Switch checked={groupByType} onChange={handleToggleGroupByType} />
              }
              label="Group by Type"
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <BeerStylesTable
              beerStyles={displayedStyles}
              groupByType={groupByType}
              onEditClick={handleEditClick}
            />
          </>
        )}
      </Container>
      <EditBeerStyleDialog
        open={editDialogOpen}
        submitting={submitting}
        errorMsg={errorMsg}
        beerStyle={editingStyle ? { ...editingStyle, ...formData } : null}
        isCreateMode={isCreateMode}
        onClose={handleCloseDialog}
        onChange={handleFormChange}
        onSave={handleSave}
        styleTypes={styleTypes}
      />
    </DashboardLayout>
  );
}

export default BeerStyles;

